import { useEffect, useState } from 'preact/hooks';
import {
  platformBilling,
  Subscription,
  Invoice,
  Plan,
  formatMoney,
  bpsToPercent,
} from './billingApi';
import { StepUpModal, StepUpRequiredError, runWithStepUp } from './StepUpModal';
import { Modal } from './BillingPage';
import { getAdminToken } from './api';

/**
 * OrgBillingTab is the per-org billing surface on the organization detail
 * page. Shows: current subscription, assign-plan dropdown, complimentary
 * toggle, close-period button, invoice table, credit issuance. Invoices
 * open in a detail modal with line items + PDF download.
 */
export function OrgBillingTab({ orgId }: { orgId: string }) {
  const [sub, setSub] = useState<Subscription | null | undefined>(undefined);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [stepUpFor, setStepUpFor] = useState<(() => void) | null>(null);
  const [showAssign, setShowAssign] = useState(false);
  const [showCredit, setShowCredit] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  async function load() {
    setErr(null);
    try {
      const [subResp, plansResp, invResp] = await Promise.all([
        platformBilling.getOrgSubscription(orgId),
        platformBilling.listPlans(false),
        platformBilling.listInvoicesForOrg(orgId),
      ]);
      setSub(subResp.subscription || null);
      setPlans((plansResp.plans || []).filter((p) => p.status === 'active'));
      setInvoices(invResp.invoices || []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  async function runAction(fn: () => Promise<void>) {
    try {
      await runWithStepUp(fn);
      await load();
    } catch (e) {
      if (e instanceof StepUpRequiredError) {
        setStepUpFor(() => () => runAction(fn));
        return;
      }
      setErr(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  if (sub === undefined) return <p>Loading…</p>;
  if (err) return <div class="error-banner">{err}</div>;

  return (
    <div>
      {info && (
        <div
          style={{
            background: 'rgba(79,209,139,0.15)',
            border: '1px solid var(--admin-success)',
            color: '#c2f2d6',
            padding: 12,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {info}
        </div>
      )}

      {/* Current subscription */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ marginTop: 0 }}>Subscription</h2>
        {!sub ? (
          <div>
            <p style={{ color: 'var(--admin-text-dim)' }}>
              This org has no subscription. Assign a plan to begin billing.
            </p>
            <button class="primary" onClick={() => setShowAssign(true)} disabled={plans.length === 0}>
              Assign plan
            </button>
            {plans.length === 0 && (
              <p style={{ color: 'var(--admin-text-dim)', fontSize: 13, marginTop: 8 }}>
                No active plans available — create one first under Billing → Plans.
              </p>
            )}
          </div>
        ) : (
          <div>
            <table class="admin-table" style={{ marginBottom: 12 }}>
              <tbody>
                <tr>
                  <td style={{ width: 200, color: 'var(--admin-text-dim)' }}>Plan</td>
                  <td>
                    <strong>{sub.planSlug}</strong>{' '}
                    <span style={{ color: 'var(--admin-text-dim)' }}>v{sub.planVersion}</span>
                  </td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--admin-text-dim)' }}>Status</td>
                  <td>
                    <span
                      class={`badge ${
                        sub.status === 'active' || sub.status === 'trialing'
                          ? 'success'
                          : sub.status === 'past_due' || sub.status === 'suspended'
                          ? 'failure'
                          : ''
                      }`}
                    >
                      {sub.status}
                    </span>
                    {sub.complimentary && (
                      <span
                        class="badge"
                        style={{
                          marginLeft: 8,
                          background: 'rgba(240,180,41,0.15)',
                          color: 'var(--admin-warning)',
                        }}
                      >
                        complimentary
                      </span>
                    )}
                  </td>
                </tr>
                {sub.complimentary && sub.complimentaryReason && (
                  <tr>
                    <td style={{ color: 'var(--admin-text-dim)' }}>Complimentary reason</td>
                    <td>{sub.complimentaryReason}</td>
                  </tr>
                )}
                <tr>
                  <td style={{ color: 'var(--admin-text-dim)' }}>Current period</td>
                  <td class="mono">
                    {new Date(sub.currentPeriodStart).toLocaleDateString()} →{' '}
                    {new Date(sub.currentPeriodEnd).toLocaleDateString()}
                  </td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--admin-text-dim)' }}>Payment method</td>
                  <td class="mono">{sub.paymentMethodState}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--admin-text-dim)' }}>Dunning</td>
                  <td class="mono">{sub.dunningState}</td>
                </tr>
              </tbody>
            </table>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => setShowAssign(true)}>Change plan</button>
              <button
                onClick={() => {
                  const toggle = !sub.complimentary;
                  const reason = window.prompt(
                    toggle ? 'Why mark complimentary?' : 'Why remove complimentary flag?',
                    '',
                  );
                  if (reason === null) return;
                  runAction(async () => {
                    await platformBilling.setComplimentary({
                      orgId,
                      complimentary: toggle,
                      reason,
                    });
                    setInfo(toggle ? 'Marked complimentary.' : 'Complimentary flag cleared.');
                  });
                }}
              >
                {sub.complimentary ? 'Clear complimentary' : 'Mark complimentary'}
              </button>
              <button
                onClick={() => {
                  const reason = window.prompt(
                    'Close the current period and generate an invoice? Reason (audit-logged):',
                    '',
                  );
                  if (reason === null) return;
                  if (!reason.trim()) {
                    alert('Reason required.');
                    return;
                  }
                  runAction(async () => {
                    const r = await platformBilling.closePeriod(orgId, reason);
                    setInfo(
                      `Invoice ${r.invoice.number} generated (${r.invoice.status}). ${r.ledgerRowsRolled} ledger row(s) rolled up${r.creditApplied ? ', complimentary credit applied' : ''}.`,
                    );
                  });
                }}
              >
                Close period → generate invoice
              </button>
              <button onClick={() => setShowCredit(true)}>Issue credit</button>
            </div>
          </div>
        )}
      </section>

      {/* Invoices */}
      <section>
        <h2>Invoices</h2>
        {invoices.length === 0 ? (
          <p style={{ color: 'var(--admin-text-dim)' }}>No invoices yet.</p>
        ) : (
          <table class="admin-table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Period</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Subtotal</th>
                <th style={{ textAlign: 'right' }}>Credit</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th>Issued</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} onClick={() => setSelectedInvoice(inv)} style={{ cursor: 'pointer' }}>
                  <td class="mono">{inv.number || <em>(draft)</em>}</td>
                  <td class="mono">{inv.billingPeriod}</td>
                  <td>
                    <span
                      class={`badge ${
                        inv.status === 'paid' || inv.status === 'written_off'
                          ? 'success'
                          : inv.status === 'void' || inv.status === 'uncollectible'
                          ? 'failure'
                          : ''
                      }`}
                    >
                      {inv.status}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }} class="mono">
                    {formatMoney(inv.subtotalCents, inv.currency)}
                  </td>
                  <td style={{ textAlign: 'right' }} class="mono">
                    {Number(inv.creditAppliedCents) > 0
                      ? '-' + formatMoney(inv.creditAppliedCents, inv.currency)
                      : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }} class="mono">
                    {formatMoney(inv.totalCents, inv.currency)}
                  </td>
                  <td class="mono">
                    {inv.issuedAt ? new Date(inv.issuedAt).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {showAssign && (
        <AssignPlanModal
          orgId={orgId}
          plans={plans}
          currentPlanId={sub?.planId}
          onClose={() => setShowAssign(false)}
          onAssigned={() => {
            setShowAssign(false);
            setInfo('Plan assigned.');
            load();
          }}
          onStepUp={(retry) => {
            setShowAssign(false);
            setStepUpFor(() => retry);
          }}
        />
      )}

      {showCredit && (
        <IssueCreditModal
          orgId={orgId}
          invoices={invoices}
          onClose={() => setShowCredit(false)}
          onIssued={() => {
            setShowCredit(false);
            setInfo('Credit issued.');
            load();
          }}
          onStepUp={(retry) => {
            setShowCredit(false);
            setStepUpFor(() => retry);
          }}
        />
      )}

      {selectedInvoice && (
        <InvoiceDetailModal
          invoiceId={selectedInvoice.id}
          onClose={() => setSelectedInvoice(null)}
        />
      )}

      {stepUpFor && (
        <StepUpModal
          prompt="This action requires password re-entry."
          onSuccess={() => {
            const retry = stepUpFor;
            setStepUpFor(null);
            retry();
          }}
          onCancel={() => setStepUpFor(null)}
        />
      )}
    </div>
  );
}

// ─── Assign plan ───

function AssignPlanModal({
  orgId,
  plans,
  currentPlanId,
  onClose,
  onAssigned,
  onStepUp,
}: {
  orgId: string;
  plans: Plan[];
  currentPlanId?: string;
  onClose(): void;
  onAssigned(): void;
  onStepUp(retry: () => void): void;
}) {
  const [planId, setPlanId] = useState(currentPlanId || plans[0]?.id || '');
  const [complimentary, setComplimentary] = useState(false);
  const [complimentaryReason, setComplimentaryReason] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    const assign = async () => {
      await platformBilling.assignPlanToOrg({
        orgId,
        planId,
        complimentary,
        complimentaryReason: complimentary ? complimentaryReason : undefined,
        reason,
      });
      onAssigned();
    };
    try {
      await runWithStepUp(assign);
    } catch (e) {
      if (e instanceof StepUpRequiredError) {
        onStepUp(async () => {
          try {
            await runWithStepUp(assign);
          } catch (err) {
            setErr(err instanceof Error ? err.message : 'Unknown error');
            setSubmitting(false);
          }
        });
        return;
      }
      setErr(e instanceof Error ? e.message : 'Unknown error');
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Assign plan" onClose={onClose}>
      {err && <div class="error-banner">{err}</div>}
      <form onSubmit={submit}>
        <div class="field">
          <label>Plan</label>
          <select required value={planId} onChange={(e) => setPlanId((e.target as HTMLSelectElement).value)}>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.slug} v{p.version}) — {formatMoney(p.recurringFeeCents, p.currency)}/
                {p.billingInterval}, {bpsToPercent(p.commissionBps)} commission
              </option>
            ))}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <input
            type="checkbox"
            checked={complimentary}
            onChange={(e) => setComplimentary((e.target as HTMLInputElement).checked)}
          />
          <span>Mark complimentary (invoice generates but is fully credited)</span>
        </label>
        {complimentary && (
          <div class="field">
            <label>Complimentary reason</label>
            <input
              required
              value={complimentaryReason}
              onInput={(e) => setComplimentaryReason((e.target as HTMLInputElement).value)}
              placeholder="e.g. MomentPix-self, partner comp, pilot trial"
            />
          </div>
        )}
        <div class="field">
          <label>Reason (audit)</label>
          <input
            required
            value={reason}
            onInput={(e) => setReason((e.target as HTMLInputElement).value)}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button class="primary" type="submit" disabled={submitting}>
            {submitting ? 'Assigning…' : 'Assign plan'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Issue credit ───

function IssueCreditModal({
  orgId,
  invoices,
  onClose,
  onIssued,
  onStepUp,
}: {
  orgId: string;
  invoices: Invoice[];
  onClose(): void;
  onIssued(): void;
  onStepUp(retry: () => void): void;
}) {
  const [amountCents, setAmountCents] = useState(0);
  const [reason, setReason] = useState('goodwill');
  const [notes, setNotes] = useState('');
  const [applyToInvoiceId, setApplyToInvoiceId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    if (amountCents <= 0) {
      setErr('Amount must be positive (in cents).');
      return;
    }
    setErr(null);
    setSubmitting(true);
    const issue = async () => {
      await platformBilling.issueCredit({
        orgId,
        amountCents,
        reason,
        notes: notes || undefined,
        applyToInvoiceId: applyToInvoiceId || undefined,
      });
      onIssued();
    };
    try {
      await runWithStepUp(issue);
    } catch (e) {
      if (e instanceof StepUpRequiredError) {
        onStepUp(async () => {
          try {
            await runWithStepUp(issue);
          } catch (err) {
            setErr(err instanceof Error ? err.message : 'Unknown error');
            setSubmitting(false);
          }
        });
        return;
      }
      setErr(e instanceof Error ? e.message : 'Unknown error');
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Issue credit" onClose={onClose}>
      <p style={{ color: 'var(--admin-text-dim)' }}>
        Credits offset future or existing invoices. A credit without an
        apply-to-invoice target sits on the org until the next period close.
      </p>
      {err && <div class="error-banner">{err}</div>}
      <form onSubmit={submit}>
        <div class="field">
          <label>Amount (cents)</label>
          <input
            type="number"
            required
            min={1}
            value={String(amountCents)}
            onInput={(e) => setAmountCents(Number((e.target as HTMLInputElement).value))}
          />
          <div style={{ fontSize: 12, color: 'var(--admin-text-dim)', marginTop: 4 }}>
            {formatMoney(amountCents)}
          </div>
        </div>
        <div class="field">
          <label>Reason</label>
          <select value={reason} onChange={(e) => setReason((e.target as HTMLSelectElement).value)}>
            <option value="goodwill">goodwill</option>
            <option value="service_failure">service_failure</option>
            <option value="manual">manual</option>
            <option value="refund_offset">refund_offset</option>
            <option value="chargeback_reversal">chargeback_reversal</option>
          </select>
        </div>
        <div class="field">
          <label>Notes</label>
          <input value={notes} onInput={(e) => setNotes((e.target as HTMLInputElement).value)} />
        </div>
        <div class="field">
          <label>Apply to invoice (optional)</label>
          <select
            value={applyToInvoiceId}
            onChange={(e) => setApplyToInvoiceId((e.target as HTMLSelectElement).value)}
          >
            <option value="">— Don't apply (sits unapplied) —</option>
            {invoices
              .filter((inv) => ['open', 'partially_paid'].includes(inv.status))
              .map((inv) => (
                <option key={inv.id} value={inv.id}>
                  {inv.number} — {formatMoney(inv.totalCents, inv.currency)} {inv.status}
                </option>
              ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button class="primary" type="submit" disabled={submitting}>
            {submitting ? 'Issuing…' : 'Issue credit'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Invoice detail ───

function InvoiceDetailModal({ invoiceId, onClose }: { invoiceId: string; onClose(): void }) {
  const [inv, setInv] = useState<Invoice | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const [pending, setPending] = useState(false);
  const [stepUpFor, setStepUpFor] = useState<(() => void) | null>(null);

  async function load() {
    setErr(null);
    try {
      const r = await platformBilling.getInvoice(invoiceId);
      setInv(r.invoice);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  async function render() {
    setRendering(true);
    setErr(null);
    try {
      const r = await platformBilling.renderInvoicePDF(invoiceId);
      setInv(r.invoice);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setRendering(false);
    }
  }

  async function downloadInvoicePDF(id: string, number: string) {
    setErr(null);
    try {
      const token = getAdminToken();
      if (!token) {
        setErr('Not authenticated. Please sign in again.');
        return;
      }
      const resp = await fetch(`/admin/invoices/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const body = await resp.text().catch(() => '');
        setErr(`Download failed: ${resp.status} ${body}`);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${number || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Download error');
    }
  }

  async function runAction(fn: () => Promise<string | null>) {
    setErr(null);
    setInfo(null);
    setPending(true);
    try {
      const msg = await runWithStepUp(fn);
      if (msg) setInfo(msg);
      await load();
    } catch (e) {
      if (e instanceof StepUpRequiredError) {
        setStepUpFor(() => () => runAction(fn));
        setPending(false);
        return;
      }
      setErr(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setPending(false);
    }
  }

  function retryCharge() {
    const reason = window.prompt('Reason for retry (audit-captured):', 'manual retry');
    if (reason === null) return;
    runAction(async () => {
      const r = await platformBilling.retryCharge(invoiceId, reason);
      if (r.chargeStatus === 'succeeded') {
        return `Charge succeeded. Invoice now ${r.invoice.status}.`;
      }
      if (r.error) return `Retry attempted; Stripe responded: ${r.error}`;
      return `Retry attempted (status: ${r.chargeStatus}).`;
    });
  }

  function voidInvoice() {
    const reason = window.prompt(
      'Void this invoice? This also un-assigns its ledger rows so they roll into the next period.\n\nReason (required):',
      '',
    );
    if (!reason?.trim()) return;
    runAction(async () => {
      await platformBilling.voidInvoice(invoiceId, reason);
      return `Invoice voided.`;
    });
  }

  function refundInvoice() {
    if (!inv) return;
    const maxRefundable = Number(inv.amountPaidCents) - Number(inv.amountRefundedCents);
    const amountStr = window.prompt(
      `Refund amount in cents (0 = full remaining refundable = ${maxRefundable}):`,
      '0',
    );
    if (amountStr === null) return;
    const amount = parseInt(amountStr, 10);
    if (isNaN(amount) || amount < 0) {
      setErr('Invalid amount.');
      return;
    }
    const reason = window.prompt('Reason for refund:', 'customer request');
    if (!reason?.trim()) return;
    runAction(async () => {
      const r = await platformBilling.refundInvoice(invoiceId, amount, reason);
      return `Refund processed. Stripe refund id: ${r.refundId}.`;
    });
  }

  function openInStripe() {
    if (!inv) return;
    if (!inv.pdfStorageKey && !(inv as any).platformStripeInvoiceId) {
      // pdfStorageKey not the right test, check stripe id directly
    }
    // The Stripe invoice id is on the invoice proto; not currently
    // exposed in our TS type — fetch via a quick admin query.
    // For Slice 3c-UI, we surface the local PDF; the Stripe deep-link
    // requires pulling `platform_stripe_invoice_id` into the proto,
    // which is a trivial future polish. Leave hook here.
    setInfo('Stripe studio link pending (needs platform_stripe_invoice_id field in proto response).');
  }

  return (
    <Modal title={inv ? `Invoice ${inv.number}` : 'Invoice'} onClose={onClose}>
      {err && <div class="error-banner">{err}</div>}
      {!inv ? (
        <p>Loading…</p>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', marginBottom: 16 }}>
            <div style={{ color: 'var(--admin-text-dim)' }}>Status</div>
            <div>
              <span
                class={`badge ${
                  inv.status === 'paid' || inv.status === 'written_off'
                    ? 'success'
                    : inv.status === 'void' || inv.status === 'uncollectible'
                    ? 'failure'
                    : ''
                }`}
              >
                {inv.status}
              </span>
            </div>
            <div style={{ color: 'var(--admin-text-dim)' }}>Period</div>
            <div class="mono">
              {new Date(inv.periodStart).toLocaleDateString()} →{' '}
              {new Date(inv.periodEnd).toLocaleDateString()}
            </div>
            <div style={{ color: 'var(--admin-text-dim)' }}>Issued</div>
            <div class="mono">{inv.issuedAt ? new Date(inv.issuedAt).toLocaleString() : '—'}</div>
            {inv.dueAt && (
              <>
                <div style={{ color: 'var(--admin-text-dim)' }}>Due</div>
                <div class="mono">{new Date(inv.dueAt).toLocaleString()}</div>
              </>
            )}
            {inv.paidAt && (
              <>
                <div style={{ color: 'var(--admin-text-dim)' }}>Paid</div>
                <div class="mono">{new Date(inv.paidAt).toLocaleString()}</div>
              </>
            )}
            {inv.writtenOffAt && (
              <>
                <div style={{ color: 'var(--admin-warning)' }}>Written off</div>
                <div class="mono">{new Date(inv.writtenOffAt).toLocaleString()}</div>
              </>
            )}
          </div>

          <h3>Line items</h3>
          <table class="admin-table" style={{ marginBottom: 16 }}>
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(inv.items || []).map((it) => (
                <tr key={it.ordinal}>
                  <td>{it.description}</td>
                  <td style={{ textAlign: 'right' }} class="mono">
                    {it.quantity} {it.unit}
                  </td>
                  <td style={{ textAlign: 'right' }} class="mono">
                    {formatMoney(it.amountCents, inv.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2} style={{ textAlign: 'right', color: 'var(--admin-text-dim)' }}>
                  Subtotal
                </td>
                <td style={{ textAlign: 'right' }} class="mono">
                  {formatMoney(inv.subtotalCents, inv.currency)}
                </td>
              </tr>
              {Number(inv.creditAppliedCents) > 0 && (
                <tr>
                  <td colSpan={2} style={{ textAlign: 'right', color: 'var(--admin-accent)' }}>
                    Credit applied
                  </td>
                  <td style={{ textAlign: 'right' }} class="mono">
                    -{formatMoney(inv.creditAppliedCents, inv.currency)}
                  </td>
                </tr>
              )}
              <tr>
                <td colSpan={2} style={{ textAlign: 'right', fontWeight: 600 }}>
                  Total
                </td>
                <td style={{ textAlign: 'right', fontWeight: 600 }} class="mono">
                  {formatMoney(inv.totalCents, inv.currency)}
                </td>
              </tr>
            </tfoot>
          </table>

          {info && (
            <div style={{
              background: 'rgba(79,209,139,0.15)',
              border: '1px solid var(--admin-success)',
              color: '#c2f2d6',
              padding: 12,
              borderRadius: 8,
              margin: '12px 0',
            }}>{info}</div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {inv.pdfStorageKey ? (
              <button class="primary" onClick={() => downloadInvoicePDF(inv.id, inv.number)} disabled={pending}>
                Download PDF
              </button>
            ) : (
              <button class="primary" onClick={render} disabled={rendering || pending}>
                {rendering ? 'Rendering…' : 'Render PDF'}
              </button>
            )}
            {inv.pdfStorageKey && (
              <button onClick={render} disabled={rendering || pending}>
                {rendering ? 'Re-rendering…' : 'Re-render'}
              </button>
            )}

            {/* Phase 3c-UI operational actions. */}
            {(inv.status === 'open' || inv.status === 'partially_paid') && (
              <button onClick={retryCharge} disabled={pending}>
                {pending ? 'Working…' : 'Retry charge'}
              </button>
            )}
            {(inv.status === 'open' || inv.status === 'draft' || inv.status === 'partially_paid') && (
              <button onClick={voidInvoice} disabled={pending} style={{ color: 'var(--admin-danger)' }}>
                Void
              </button>
            )}
            {inv.status === 'paid' && Number(inv.amountPaidCents) > Number(inv.amountRefundedCents) && (
              <button onClick={refundInvoice} disabled={pending} style={{ color: 'var(--admin-danger)' }}>
                Refund
              </button>
            )}
          </div>

          {stepUpFor && (
            <StepUpModal
              prompt="This action requires password re-entry."
              onSuccess={() => {
                const retry = stepUpFor;
                setStepUpFor(null);
                retry();
              }}
              onCancel={() => setStepUpFor(null)}
            />
          )}
        </>
      )}
    </Modal>
  );
}
